import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '../../context/I18nContext';
import { useLeadOptions } from '../../utils/leadOptions';
import { Lead, LeadStatus } from '../../types';
import BaseFormModal from '../ui/BaseFormModal';
import Input from '../ui/Input';
import TextArea from '../ui/TextArea';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import CategorySelectWithAdd from './CategorySelectWithAdd';
import { ArrowRight } from 'lucide-react';
import { commitFormSave } from '../../utils/commitFormSave';

interface LeadFormData {
  name: string;
  contact_type: string; // Keep for backward compatibility
  contact_type_id: number | '';
  contact_value: string;
  sector_id: number | '';
  status: LeadStatus;
  notes: string;
  direction: 'inbound' | 'outbound' | '';
  nextActionAt: string;
  nextActionAtDate?: Date | null;
}

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => void;
  lead?: Lead | null;
  /** Prefill name when creating from LeadSearch (create mode only). */
  defaultName?: string;
  /** Called when a sector/contact_type is deleted and leads were migrated (so parent can refresh leads list). */
  onLeadsAffected?: () => void;
  /**
   * Guided flow: save lead (without the manual auto-status), close modal,
   * then highlight/open the row ⋯ menu so the user learns where to create a project.
   */
  onGuideCreateProject?: (lead: Lead) => void;
  /**
   * When true, await onSave then close (create-from-search: selection before focus restore).
   */
  closeAfterSave?: boolean;
}

const LeadModal: React.FC<LeadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  lead,
  defaultName = '',
  onLeadsAffected,
  onGuideCreateProject,
  closeAfterSave = false,
}) => {
  const { t } = useI18n();
  const { statusOptions, directionOptions } = useLeadOptions();
  const [previousStatus, setPreviousStatus] = useState<LeadStatus | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<LeadFormData>();

  useEffect(() => {
    if (isOpen) {
      if (lead) {
        setValue('name', lead.name);
        setValue('contact_type', lead.contact_type || 'phone');
        setValue('contact_type_id', lead.contact_type_id ?? '');
        setValue('contact_value', lead.contact_value || '');
        setValue('sector_id', lead.sector_id ?? '');
        setValue('status', lead.status);
        setPreviousStatus(lead.status);
        setValue('notes', lead.notes || '');
        setValue('direction', lead.direction || '');
        const nextActionDate = lead.nextActionAt ? new Date(lead.nextActionAt) : null;
        if (nextActionDate) {
          const y = nextActionDate.getFullYear();
          const m = String(nextActionDate.getMonth() + 1).padStart(2, '0');
          const d = String(nextActionDate.getDate()).padStart(2, '0');
          setValue('nextActionAt', `${y}-${m}-${d}`);
        } else {
          setValue('nextActionAt', '');
        }
        setValue('nextActionAtDate', nextActionDate);
      } else {
        reset({
          name: defaultName.trim(),
          contact_type: 'phone',
          contact_type_id: '',
          contact_value: '',
          sector_id: '',
          status: 'to_contact',
          notes: '',
          direction: '',
          nextActionAt: '',
          nextActionAtDate: null,
        });
        setPreviousStatus('to_contact');
      }
    }
  }, [isOpen, lead, defaultName, setValue, reset]);

  const onSubmit = async (data: LeadFormData) => {
    const contactValueTrimmed = data.contact_value?.trim();
    // Type + value go together: only persist when contact_value is filled; otherwise store both as null
    const contact_type_id = contactValueTrimmed && data.contact_type_id
      ? parseInt(data.contact_type_id.toString())
      : undefined;
    const contact_value = contactValueTrimmed || undefined;

    const leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'> = {
      name: data.name,
      contact_type: data.contact_type as string, // Keep for backward compatibility
      contact_type_id,
      contact_value,
      sector_id: data.sector_id ? parseInt(data.sector_id.toString()) : undefined,
      status: data.status,
      notes: data.notes,
      direction: data.direction || undefined,
      lastActionAt: new Date(),
      nextActionAt: data.nextActionAt
        ? (() => {
            const [year, month, day] = data.nextActionAt.split('-').map(Number);
            return new Date(year, month - 1, day);
          })()
        : null,
      followupCount: lead?.followupCount || 0,
    };

    await commitFormSave({
      closeAfterSave,
      onSave: () => onSave(leadData),
      onClose,
      setSaving: setIsSaving,
    });
  };

  const saveAndGuideCreateProject = () => {
    if (!lead || previousStatus == null || !onGuideCreateProject) return;
    handleSubmit((data) => {
      onSubmit({ ...data, status: previousStatus });
      onGuideCreateProject(lead);
    })();
  };

  return (
    <>
      <BaseFormModal
        isOpen={isOpen}
        onClose={onClose}
        resourceKey="leads"
        size="xl"
        isLoading={isSaving}
        mode={lead ? 'update' : 'create'}
        onValidate={handleSubmit(onSubmit)}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        {/* Register non-input fields for validation */}
        <input type="hidden" {...register('sector_id')} />
        <input type="hidden" {...register('contact_type_id')} />
        <input type="hidden" {...register('status', { required: t('leads.statusRequired') })} />
        {/* Top row: 3 cols on desktop for better balance */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={t('leads.name')}
            {...register('name', { required: t('leads.nameRequired') })}
            error={errors.name?.message}
            placeholder={t('leads.namePlaceholder')}
          />
          
          <CategorySelectWithAdd
            type="sector"
            label={t('leads.sector')}
            value={watch('sector_id')?.toString() || ''}
            onChange={(value) =>
              setValue('sector_id', value ? parseInt(value) : '', { shouldValidate: true, shouldDirty: true })
            }
            error={errors.sector_id?.message}
            addPlaceholder={t('leads.newSector')}
            onDeleteWithMigrationComplete={onLeadsAffected}
          />

          <Select
            label={t('leads.direction')}
            value={watch('direction') || ''}
            onChange={(value) => setValue('direction', value as 'inbound' | 'outbound' | '')}
            options={directionOptions}
            error={errors.direction?.message}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CategorySelectWithAdd
          type="contact_type"
          label={t('leads.contactType')}
          value={watch('contact_type_id')?.toString() || ''}
          onChange={(value) =>
            setValue('contact_type_id', value ? parseInt(value) : '', { shouldValidate: true, shouldDirty: true })
          }
          error={errors.contact_type_id?.message}
          addPlaceholder={t('leads.newContactType')}
          onDefaultReady={(id) =>
            setValue('contact_type_id', id, { shouldValidate: true, shouldDirty: true })
          }
          onDeleteWithMigrationComplete={onLeadsAffected}
        />
          
          <Input
            label={t('leads.contactValue')}
            {...register('contact_value')}
            error={errors.contact_value?.message}
            placeholder={t('leads.contactValuePlaceholder')}
          />
        </div>

        {/* Status + Next follow-up should take full width together */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label={t('leads.status')}
            value={watch('status') || ''}
            onChange={(value) => {
              const newStatus = value as LeadStatus;
              const oldStatus = watch('status') as LeadStatus;
              // Store old status BEFORE updating (for change detection)
              setPreviousStatus(oldStatus);
              setValue('status', newStatus, { shouldValidate: true, shouldDirty: true });
            }}
            options={statusOptions}
            error={errors.status?.message}
          />
          
          
          <DatePicker
            label={t('leads.nextFollowUp')}
            value={watch('nextActionAtDate') || watch('nextActionAt') || null}
            onChange={(date) => {
              setValue('nextActionAtDate', date);
              if (date) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                setValue('nextActionAt', `${y}-${m}-${d}`);
              } else {
                setValue('nextActionAt', '');
              }
            }}
            error={errors.nextActionAt?.message}
          />
        </div>

        {/* Warning: auto statuses — guide toward creating a project */}
        {(watch('status') === 'in_production' || watch('status') === 'completed') &&
         previousStatus !== null &&
         previousStatus !== watch('status') && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <span
                dangerouslySetInnerHTML={{
                  __html: t('leads.autoStatusWarning', {
                    status1: `<strong>${t('leads.statusInProduction')}</strong>`,
                    status2: `<strong>${t('leads.statusCompleted')}</strong>`,
                  }),
                }}
              />
              {lead && onGuideCreateProject && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={saveAndGuideCreateProject}
                    className="inline-flex items-center gap-0.5 font-semibold text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    {t('leads.autoStatusCreateProject')}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </button>
                </>
              )}
            </p>
          </div>
        )}

        <TextArea
          label={t('leads.notes')}
          {...register('notes')}
          rows={4}
          placeholder={t('leads.notesPlaceholder')}
        />

        </form>
      </BaseFormModal>

    </>
  );
};
  
  export default LeadModal;
